import {
  count,
  index,
  get,
  post,
  put,
  upload,
  getFiles,
  putStatus,
  checkNumber,
} from "src/api/purchase-order";
import * as statusApi from "src/api/status";

import PurchaseOrderForm from "src/modules/purchase-order/components/forms/PurchaseOrderForm.vue";
import UploadForm from "src/modules/purchase-order/components/forms/UploadForm.vue";
import ItemForm from "src/modules/purchase-order/components/forms/ItemForm.vue";

import { findIndex } from "src/assets/scripts/utilities/helpers";

const getInitialState = () => {
  return {
    counts: {},
    purchaseOrders: [],
    purchaseOrder: {},
    purchaseOrderStatuses: [],
  };
};

const preparePurchaseOrder = (purchaseOrder) => {
  const { buyer, created_by, owner, rfq, associated_agent, ...rest } =
    purchaseOrder;

  purchaseOrder = {
    ...rest,
    buyer: buyer?._id || buyer,
    created_by: created_by?._id || created_by,
    owner: owner?._id || owner,
    associated_agent: associated_agent?._id || associated_agent,
    rfq: rfq?._id || rfq,
  };

  return purchaseOrder;
};

export const usePurchaseOrderStore = defineStore("purchase-order", () => {
  const formState = ref({
    edit: false,
    open: false,
  });

  const togglePurchaseOrderForm = (state = {}) => {
    state = { edit: false, open: !formState.value.open, ...state };

    const { toggleRightDrawer } = useAppStore();

    toggleRightDrawer(state.open, PurchaseOrderForm);

    formState.value = state;
  };

  const itemFormState = ref({
    edit: false,
    open: false,
  });

  const toggleItemForm = (state = {}) => {
    state = { edit: false, open: !itemFormState.value.open, ...state };

    const { toggleNestedRightDrawer } = useAppStore();

    toggleNestedRightDrawer({
      component: ItemForm,
      props: state.props,
      open: state.open,
    });

    itemFormState.value = state;
  };

  const uploadFormState = ref({
    open: false,
  });

  const toggleUploadForm = (state = {}) => {
    state = { open: !uploadFormState.value.open, ...state };

    const { toggleNestedRightDrawer } = useAppStore();

    toggleNestedRightDrawer({
      component: UploadForm,
      props: state.props,
      open: state.open,
    });

    uploadFormState.value = state;
  };

  const uploadPurchaseOrder = async (payload) => {
    try {
      const response = await upload(payload);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  let state = reactive(getInitialState());

  const getCounts = async (id = state.purchaseOrder?._id) => {
    try {
      const response = await count(id);

      state.counts = response?.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const setPurchaseOrder = (updatedPurchaseOrder = state.purchaseOrder) => {
    const updatedPoIndex = findIndex(
      state.purchaseOrders,
      updatedPurchaseOrder,
      "_id",
    );

    const listedPo = state.purchaseOrders[updatedPoIndex];

    delete listedPo?.files;

    delete updatedPurchaseOrder?.files;

    const { files = [] } = state.purchaseOrder;

    state.purchaseOrder = {
      files,
      ...listedPo,
      ...updatedPurchaseOrder,
    };
  };

  const getPurchaseOrder = async (id) => {
    try {
      const response = await get(id);

      setPurchaseOrder(response?.data?.data);
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getPurchaseOrderFiles = async (options = {}) => {
    try {
      if (!state?.purchaseOrder?._id) {
        return;
      }

      options.params = {
        ...options?.params,
        page_size: state.counts["files_count"] || 25,
        po_id: state.purchaseOrder?._id,
      };

      const response = await getFiles(options);

      state.purchaseOrder.files = response?.data?.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getPurchaseOrderStatuses = async (options) => {
    try {
      const response = await statusApi.index(options);

      state.purchaseOrderStatuses = response?.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    status_cd: null,
    created_by: null,
    owner: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getPurchaseOrders = async (options = {}) => {
    try {
      const response = await index({
        params: params.value,
        ...unref(options),
      });

      state.purchaseOrders = response?.data?.data?.data;

      pagination.value.rowsNumber = response?.data?.data?.count;

      setPurchaseOrder();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const postPurchaseOrder = async (purchaseOrder) => {
    try {
      const response = await post(preparePurchaseOrder(purchaseOrder));

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putPurchaseOrder = async (purchaseOrder) => {
    try {
      const response = await put(preparePurchaseOrder(purchaseOrder));

      if (response?.data?.data) {
        state.purchaseOrder = response.data.data;
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const addFile = (file) => {
    const poIndex = findIndex(state.purchaseOrders, { _id: file.po_id }, "_id");

    if (poIndex !== -1) {
      state.purchaseOrders[poIndex].files_count += 1;

      state.purchaseOrders[poIndex].files.unshift(file);
    }
  };

  const updateFile = (file) => {
    const fileIndex = findIndex(
      state.purchaseOrder.files,
      { _id: file._id },
      "_id",
    );

    if (fileIndex !== -1) {
      state.purchaseOrder.files[fileIndex] = file;
    }
  };

  const fileStore = useFileStore();

  const removeFile = async (_id, user_id) => {
    state.purchaseOrder.files_count -= 1;

    const response = await fileStore.removeFile(_id, user_id);

    await getPurchaseOrderFiles();

    return response;
  };

  const restoreFile = async (_id, user_id) => {
    state.purchaseOrder.files_count += 1;

    const response = await fileStore.restoreFile(_id, user_id);

    await getPurchaseOrderFiles();

    return response;
  };

  const putPurchaseOrderStatus = async (purchaseOrder) => {
    try {
      const response = await putStatus(preparePurchaseOrder(purchaseOrder));

      await getPurchaseOrders();

      if (response?.data?.data) {
        const tempFiles = [...state.purchaseOrder.files];

        state.purchaseOrder = response.data.data;

        if (purchaseOrder.status !== "cancelled") {
          state.purchaseOrder.files = tempFiles;
        }
      }

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const isDuplicate = async (payload) => {
    const response = await checkNumber(payload);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    itemFormState,
    uploadFormState,
    togglePurchaseOrderForm,
    toggleUploadForm,
    toggleItemForm,

    ...toRefs(state),

    filter,
    filters,
    pagination,

    addFile,
    removeFile,
    restoreFile,
    updateFile,

    getCounts,
    getPurchaseOrder,
    getPurchaseOrderFiles,
    getPurchaseOrderStatuses,
    getPurchaseOrders,
    postPurchaseOrder,
    uploadPurchaseOrder,
    putPurchaseOrderStatus,
    putPurchaseOrder,
    setPurchaseOrder,
    isDuplicate,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(usePurchaseOrderStore, import.meta.hot),
  );
}
